import { Avatar, Box, ButtonBase, Stack, Typography } from '@mui/material'
import { Fade } from 'utoniq-ui'

import {
  GachaOrderDetailFragment,
  ItemThumbnailType,
} from '@/clients/utoniq-core/schema'
import { VoicePlayer } from '@/components/organisms/resourcePlayer/VoicePlayer'
import { getEmphasisRaritySx } from '@/components/pages/GachaResult/template/TenGachaResult/LightFrame'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { individualHideGachaRemainingItemCount } from '@/core/individual'

import { ButtonSetCell, State } from '..'

type SingleGachaResultCellProps = {
  loading: boolean
  ticket: State['ticket']
  artistId?: string
  rewardItem: GachaOrderDetailFragment['rewardItems'][number]
  gacha?: GachaOrderDetailFragment['gacha']
  rollGacha: (count: number) => void
  onSelectUserItem: (itemId: string) => void
  isPointOnly: boolean
  prepaidPointBalance: number
}
export const SingleGachaResultCell: Cell<SingleGachaResultCellProps> = ({
  loading,
  rewardItem,
  artistId,
  ticket,
  gacha,
  rollGacha,
  onSelectUserItem,
  isPointOnly,
  prepaidPointBalance,
}) => {
  const { t } = useTranslation('gachaResult')
  const { userItem, item } = rewardItem

  const voiceResource = item.avatarResources?.voiceResource

  return (
    <Fade>
      <CellFrame
        sx={{
          display: 'flex',
          flexDirection: 'column',
          pt: 2,
          pb: 3,
          px: 5,
          height: 'calc(100% - 84px)',
        }}
      >
        <Stack
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          flex={1}
        >
          <ButtonBase
            onClick={() => onSelectUserItem(item.id)}
            sx={{ position: 'relative' }}
          >
            <Box
              sx={{
                position: 'absolute',
                zIndex: 0,
                top: 0,
                width:
                  item.thumbnailType === ItemThumbnailType.Square ? 280 : 175,
                height: 280,
                ...getEmphasisRaritySx(item.rarity?.id, 50),
              }}
            />
            <Avatar
              variant="square"
              src={item.thumbnail.front?.mediumUri ?? ''}
              sx={{
                width:
                  item.thumbnailType === ItemThumbnailType.Square ? 280 : 175,
                height: 280,
              }}
            />
          </ButtonBase>
          <Typography variant="h5" sx={{ pt: 5 }}>
            {item.title}
          </Typography>
          {voiceResource && (
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                pt: 4,
              }}
            >
              <VoicePlayer
                resource={voiceResource}
                bgcolor="primary.main"
                color="white"
              />
            </Box>
          )}
          <Stack
            flexDirection="row"
            justifyContent="center"
            alignItems="center"
            sx={{ pt: 2, textAlign: 'center' }}
          >
            {item?.rarity && (
              <>
                <Typography variant="overline" sx={{ mr: 1 }}>
                  {t('rarity')}
                </Typography>
                <Avatar
                  variant="square"
                  src={item?.rarityIconImage?.smallUri ?? ''}
                  sx={{
                    height: 20,
                    width: 'fit-content',
                    mr: 1,
                  }}
                />
                <Typography variant="overline" color="text.hint" sx={{ mr: 4 }}>
                  {item?.rarity?.name}
                </Typography>
              </>
            )}

            <Typography variant="overline" color="text.hint" sx={{ mr: 2 }}>
              {t('numberOfPossessions')}
            </Typography>
            <Typography variant="overline">
              {userItem.number}
              {individualHideGachaRemainingItemCount()
                ? t('possessionUnit')
                : ` / ${item.issuedNumber ?? '∞'}`}
            </Typography>
          </Stack>
        </Stack>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'end',
          }}
        >
          <ButtonSetCell
            rollGacha={rollGacha}
            loading={loading}
            ticket={ticket}
            count={1}
            artistId={artistId}
            remainingLimitedItemCount={gacha?.remainingLimitedItemCount ?? 0}
            totalLimitedItemCount={gacha?.totalLimitedItemCount ?? 0}
            isLimitedGacha={!!gacha?.isLimited}
            isPointOnly={isPointOnly}
            price={gacha?.price ?? 0}
            prepaidPointBalance={prepaidPointBalance}
          />
        </Box>
      </CellFrame>
    </Fade>
  )
}
